import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { makeMoveable, Draggable, Resizable, Snappable, Rotatable } from 'react-moveable';
import MoveableHelper from 'moveable-helper';
import { Menu, MenuItem } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { getDefaultWidgetData } from '../../utils/helper';
import { updateWidgetPositionAction } from '../../../../actions/scenarioActions';
import { getSecondsFromTimelineTime, getTimelineTimeFromTimeInput } from '../../../../services/timeStampService';
import { QPLAYER_IDENTIFIER, WIDGET_TYPE_IFRAME } from '../../../../constants/scenarioConstant';

import { isEventMessageComeFromEditorPlayer, updateRotation } from './widgetInteractionItem/helper';
import { restartLoopAnimation } from './widgetInteractionItem/useWidgetAnimation';
import WidgetInteractionItem from './widgetInteractionItem/WidgetInteractionItem';

import './WidgetOverlay.scss';

const Moveable = makeMoveable([Snappable, Draggable, Resizable, Rotatable]);

// Stop any running widget animation on a subtree so its transform reverts to identity.
// The pill/square entry SCALE (transform: scale) lives on a <g> INSIDE react-moveable's
// resize target, so a widget caught mid-scale is measured - and therefore resized - with
// a shrunken bounding box, which collapses it toward the 1x1 min. Clearing the running
// animate.css classes reverts that <g> to full size so the resize measures correctly.
const neutralizeWidgetAnimation = (root) => {
	root?.querySelectorAll?.('.animate__animated').forEach((el) => {
		Array.from(el.classList)
			.filter((c) => c.startsWith('animate__'))
			.forEach((c) => el.classList.remove(c));
		el.style.removeProperty('animation-delay');
		el.style.removeProperty('animation-duration');
		el.style.removeProperty('animation-fill-mode');
	});
};

const WidgetOverlay = forwardRef(
	(
		{
			className = '',
			onDropWidgetOnVideoEditor,
			activeLayout = '',
			editingScene,
			editingBox,
			editingWidget,
			onWidgetClick,
			pausePlayer,
			removeWidgetInBox,
			timelineEditorRef,
			widgetSettingRef,
		},
		ref
	) => {
		const overlayBounds = useRef(null);
		const widgetOverlaySvgRef = useRef(null);
		const dispatch = useDispatch();
		const { t } = useTranslation();

		const storedDraggingElementRef = useRef(null);
		const moveableRef = useRef(null);
		// true (unscaled) foreignObject size captured at resize-start, so the new size is
		// startSize + moveable's distance - immune to the animation scaling the box.
		const resizeStartSizeRef = useRef(null);

		const [helper] = useState(() => {
			return new MoveableHelper();
		});
		const [isMaintainProportions, setIsMaintainProportions] = useState(true);
		const [focusElement, setFocusElement] = useState(null);
		const [isShowElement, setIsShowElement] = useState(false);
		const [playerTimePos, setPlayerTimePos] = useState(0);
		const [contextMenuItemRef, setContextMenuItemRef] = useState(null);

		const [isHoldShift, setHoldShift] = useState(false);

		const { width, height } = overlayBounds.current?.getBoundingClientRect() || {};

		const { layouts: scenesLayouts, widgetTemplates } = editingScene ?? { layouts: [], widgetTemplates: [] };

		const widgetsFromScene = useMemo(() => {
			return scenesLayouts.reduce((widgetsInLayout, curLayout) => {
				const { boxes } = curLayout;

				// For now we show all wigets no matter what layout it is
				const widgetInBoxes = boxes.reduce((allWidgets, curBox) => {
					const { widgets } = curBox;
					allWidgets.push(...(widgets || []));
					return allWidgets;
				}, []);

				widgetsInLayout.push(...widgetInBoxes);

				return widgetsInLayout;
			}, []);
		}, [scenesLayouts]);

		const widgetTemplateMaps = useMemo(() => {
			const result = new Map();
			widgetTemplates.forEach((template) => {
				result.set(template.id, template);
			});

			return result;
		}, [widgetTemplates]);

		const interactiveWidget = useMemo(() => {
			return widgetsFromScene
				.reduce((acc, widget) => {
					const { id, widgetTemplateId } = widget;
					const template = widgetTemplateMaps.get(widgetTemplateId);

					if (template && template.type !== 'video') {
						if (Object.keys(template.style ?? {}).length === 0) {
							template.style = getDefaultWidgetData(id, template.name, template.type).style;
						}

						acc.push({
							...template,
							...widget,
							id,
						});
					}

					return acc;
				}, [])
				.sort((widgetA, widgetB) => {
					const {
						style: { zIndex: zIndexA = 0 },
					} = widgetA;
					const {
						style: { zIndex: zIndexB = 0 },
					} = widgetB;

					return zIndexA > zIndexB ? 1 : -1;
				});
		}, [widgetsFromScene, widgetTemplateMaps]);

		const handleOnDragEnd = () => {
			handleWidgetPositionUpdate();
		};

		const handleOnResizeEnd = () => {
			handleWidgetPositionUpdate();
		};

		const handleOnRotateEnd = () => {
			handleWidgetPositionUpdate();
		};

		const handleWidgetPositionUpdate = () => {
			dispatch(updateWidgetPositionAction({ editingBox }));
		};

		// After a drag/resize/rotate we neutralized (stripped) the widget's animate.css
		// classes to measure the true box; put the idle loop back so it keeps looping.
		// A button that is actively in text-edit keeps its loop paused (the widget itself
		// resumes it on blur), so leave that one alone.
		const restoreWidgetLoop = (target) => {
			if (target?.querySelector?.('[contenteditable="true"]')) {
				return;
			}
			const animatedG = target?.querySelector?.('[data-template-id]');
			if (!animatedG) {
				return;
			}
			const templateId = animatedG.getAttribute('data-template-id');
			const widget = interactiveWidget.find((w) => w.widgetTemplateId === templateId);
			restartLoopAnimation(animatedG, widget?.style?.transformSettings);
		};

		const handleOnMessage = useCallback((event) => {
			if (
				event.data?.type === 'position' &&
				event.data.identifier === QPLAYER_IDENTIFIER &&
				isEventMessageComeFromEditorPlayer(event)
			) {
				setPlayerTimePos(event.data?.metadata?.position);
			}
			if (event.data?.type === 'set-maintain-proportion') {
				setIsMaintainProportions(
					event.data?.isMaintainProportions === undefined ? true : event.data?.isMaintainProportions
				);
			}
		}, []);

		const handleOnKeyDownInput = useCallback(
			(event) => {
				if (
					event.keyCode === 8 &&
					document.activeElement.tagName === 'BODY' &&
					focusElement?.ref &&
					focusElement?.data
				) {
					removeWidgetInBox({ ...focusElement?.data });
					setFocusElement(null);
				}

				if (event.keyCode === 16) {
					setHoldShift(true);
				}
			},
			[focusElement, removeWidgetInBox]
		);

		const handleOnKeyUpInput = useCallback((event) => {
			if (event.keyCode === 16) {
				setHoldShift(false);
			}
		}, []);

		const handleWidgetsContextMenuClick = (action) => {
			const dataset = contextMenuItemRef.current?.dataset;
			if (dataset) {
				const { widgetId, templateId } = dataset;
				timelineEditorRef.current?.updateZIndexPosition(action === 'front', widgetId, templateId);
			}
			setContextMenuItemRef(null);
		};

		useImperativeHandle(ref, () => ({
			hiddenHighlightBox: (value) => {
				setIsShowElement(value);
			},
		}));

		useEffect(() => {
			window.addEventListener('message', handleOnMessage);
			window.addEventListener('keydown', handleOnKeyDownInput);
			window.addEventListener('keyup', handleOnKeyUpInput);
			return () => {
				window.removeEventListener('message', handleOnMessage);
				window.removeEventListener('keydown', handleOnKeyDownInput);
				window.addEventListener('keyup', handleOnKeyUpInput);
			};
		}, [handleOnKeyDownInput, handleOnKeyUpInput, handleOnMessage]);

		useEffect(() => {
			setFocusElement(null);
			setIsShowElement(false);
		}, [activeLayout]);

		useEffect(() => {
			setIsShowElement(false);
			if (!editingWidget) {
				setFocusElement(null);
				return;
			}
			const { widgetTemplateId } = editingWidget;
			const element = document.getElementById(widgetTemplateId);
			const data = interactiveWidget.find((widget) => widget.id === editingWidget.id);
			if (element && element.children[0]) {
				pausePlayer?.();
				setFocusElement({ ref: { current: element.children[0] }, data: data });
				return;
			}
		}, [editingWidget, editingScene, interactiveWidget]);

		useEffect(() => {
			if (!focusElement || !focusElement?.ref?.current) {
				setIsShowElement(false);
				return;
			}

			const focusingWidgetWithTime = widgetsFromScene.find((w) => w.id === focusElement?.data?.id);
			const shouldShowMovableObject =
				playerTimePos >=
					getSecondsFromTimelineTime(getTimelineTimeFromTimeInput(focusingWidgetWithTime?.start)) &&
				playerTimePos <= getSecondsFromTimelineTime(getTimelineTimeFromTimeInput(focusingWidgetWithTime?.end));
			!shouldShowMovableObject && setFocusElement({ ...focusElement, ref: null });

			setIsShowElement(true);
		}, [focusElement, editingWidget, widgetsFromScene, playerTimePos]);

		return (
			<div
				className={className}
				onDragOver={(e) => {
					e.preventDefault();
				}}
				onDrop={(event) => {
					onDropWidgetOnVideoEditor(event, widgetOverlaySvgRef.current);
				}}
				ref={overlayBounds}
				onContextMenu={(e) => {
					e.preventDefault();
				}}
			>
				<div style={{ position: 'relative', width: '100%', height: '100%' }}>
					<svg
						width="100%"
						height="100%"
						viewBox={activeLayout !== 'mobile' || isMaintainProportions ? '0 0 1600 900' : '547 0 506 900'}
						ref={widgetOverlaySvgRef}
						id="svgWidgetOverlay"
					>
						{interactiveWidget.map((widget, idx) => {
							return (
								<WidgetInteractionItem
									key={`${widget.id}-${idx}`}
									data={widget}
									bounds={overlayBounds.current}
									onClickItem={(itemRef) => {
										pausePlayer?.();
										setFocusElement({
											ref: itemRef,
											data: widget,
										});
										setIsShowElement(true);
										onWidgetClick({
											boxId: '1',
											id: widget.id,
											widgetTemplateId: widget.widgetTemplateId,
										});
									}}
									maintainRatio={isHoldShift}
									playerTimePos={playerTimePos}
									onContextMenu={(ref, isShowWidget) => {
										isShowWidget && setContextMenuItemRef(ref);
									}}
									onUpdateLabel={(value) => {
										widgetSettingRef.current?.handleTextLabelChange({
											target: { value },
										});
									}}
								/>
							);
						})}
					</svg>
				</div>
				<Moveable
					ref={moveableRef}
					target={isShowElement && focusElement?.ref ? focusElement.ref : null}
					svgOrigin="50% 50%"
					//snap
					snappable={true}
					snapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
					verticalGuidelines={[{ pos: '50%', className: 'guideline-custom' }]}
					horizontalGuidelines={[{ pos: '50%', className: 'guideline-custom' }]}
					isDisplaySnapDigit={true}
					keepRatio={isHoldShift}
					elementSnapDirections={{
						top: true,
						left: true,
						bottom: true,
						right: true,
						center: true,
						middle: true,
					}}
					elementGuidelines={interactiveWidget.map(({ widgetTemplateId }) => ({
						element: `.widget_${widgetTemplateId}`,
						className: 'guideline-custom',
					}))}
					snapThreshold={5}
					bounds={{ left: 0, right: width, top: 0, bottom: height }}
					//drag
					draggable={true}
					onDragStart={({ target }) => {
						storedDraggingElementRef.current = target.getAttribute('class');
						target.setAttribute('class', '');
						// reset any mid-animation scale so drag/snap uses the true box
						neutralizeWidgetAnimation(target);
						moveableRef.current?.updateRect?.();
						pausePlayer?.();
					}}
					onDrag={({ transform, target }) => {
						target.style.transform = transform;
					}}
					onDragEnd={({ target }) => {
						handleOnDragEnd();
						target.setAttribute('class', storedDraggingElementRef.current ?? '');
						storedDraggingElementRef.current = null;
						restoreWidgetLoop(target);
					}}
					throttleDragRotate={isHoldShift ? 90 : 0}
					//resize
					resizable={true}
					onResizeStart={(e) => {
						const { target } = e;
						storedDraggingElementRef.current = target.getAttribute('class');
						target.setAttribute('class', '');

						// If the widget is mid-animation the pill/square SCALE has shrunk its
						// rendered box; reset it to full size and re-read moveable's rect so the
						// resize is based on the real dimensions (otherwise it collapses to 1x1).
						neutralizeWidgetAnimation(target);
						moveableRef.current?.updateRect?.();

						// capture the true (unscaled) starting size from the foreignObject itself
						const object = target.querySelector('foreignObject');
						resizeStartSizeRef.current = object
							? { w: parseFloat(object.getAttribute('width')) || 0, h: parseFloat(object.getAttribute('height')) || 0 }
							: null;

						const imageWidget = focusElement?.ref?.current.querySelector('img.image-widget');
						if (imageWidget) {
							const ratio = imageWidget.naturalWidth / imageWidget.naturalHeight;
							e.setRatio(ratio);
						}
						e.setMin([1, 1]);

						pausePlayer?.();
						helper.onResizeStart(e);
					}}
					onResize={(e) => {
						const { delta, dist, target, width, height } = e;
						const object = target.querySelector('foreignObject');

						if (object) {
							// Derive the new size from the true start size + moveable's distance
							// (dist), NOT from moveable's absolute width/height - those are computed
							// from the rendered box, which the animation scale can shrink to ~0.
							const start = resizeStartSizeRef.current;
							const newWidth = start ? Math.max(1, start.w + dist[0]) : width;
							const newHeight = start ? Math.max(1, start.h + dist[1]) : height;
							delta[0] && object.setAttribute('width', newWidth);
							delta[1] && object.setAttribute('height', newHeight);
							const currentRotation = target.style.transform.match(/rotate\((.*)deg\)/);
							helper.onResize(e);
							updateRotation(currentRotation, e);
						}
					}}
					onResizeEnd={({ target }) => {
						handleOnResizeEnd();
						target.setAttribute('class', storedDraggingElementRef.current ?? '');
						storedDraggingElementRef.current = null;
						restoreWidgetLoop(target);
					}}
					//rotate
					rotatable={focusElement?.data?.type === WIDGET_TYPE_IFRAME ? false : true}
					throttleRotate={0}
					onRotateStart={({ target }) => {
						storedDraggingElementRef.current = target.getAttribute('class');
						target.setAttribute('class', '');
						// reset any mid-animation scale so rotate uses the true box
						neutralizeWidgetAnimation(target);
						moveableRef.current?.updateRect?.();
						pausePlayer?.();
					}}
					onRotate={(e) => {
						const { target, transform } = e;
						target.style.transform = transform;
					}}
					onRotateEnd={({ target }) => {
						handleOnRotateEnd();
						target.setAttribute('class', storedDraggingElementRef.current ?? '');
						storedDraggingElementRef.current = null;
						restoreWidgetLoop(target);
					}}
				/>
				<Menu
					id="widgets-menu"
					anchorEl={contextMenuItemRef?.current}
					open={Boolean(contextMenuItemRef)}
					onClose={() => {
						setContextMenuItemRef(null);
					}}
				>
					<MenuItem
						onClick={(e) => {
							e.stopPropagation();
							handleWidgetsContextMenuClick('front');
						}}
					>
						{t('SCENARIO_SCENE_EDITOR_WIDGET_OVERLAY_BRING_TO_FRONT')}
					</MenuItem>
					<MenuItem
						onClick={(e) => {
							e.stopPropagation();
							handleWidgetsContextMenuClick('back');
						}}
					>
						{t('SCENARIO_SCENE_EDITOR_WIDGET_OVERLAY_SEND_TO_BACK')}
					</MenuItem>
				</Menu>
			</div>
		);
	}
);

export default WidgetOverlay;
